import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  GraduationCap, 
  Trophy, 
  MessageSquare, 
  Award,
  ArrowRight
} from "lucide-react";

interface WelcomeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const features = [
  {
    icon: GraduationCap,
    title: "Trilhas Técnicas",
    description: "Pratique Power BI, SQL e Python com questões de todos os níveis",
    color: "bg-blue-500/10 text-blue-500"
  },
  {
    icon: MessageSquare,
    title: "AI Tutor",
    description: "Tire dúvidas em tempo real com nosso tutor de IA especializado",
    color: "bg-purple-500/10 text-purple-500"
  },
  {
    icon: Trophy,
    title: "Ranking & Pontos",
    description: "Ganhe pontos, suba de nível e compare seu desempenho com outros",
    color: "bg-yellow-500/10 text-yellow-500"
  },
  {
    icon: Award,
    title: "Certificados",
    description: "Conquiste certificados verificáveis para mostrar suas habilidades",
    color: "bg-green-500/10 text-green-500"
  }
];

export function WelcomeModal({ open, onOpenChange }: WelcomeModalProps) {
  const [step, setStep] = useState(0);

  const handleStart = () => {
    localStorage.setItem('academy_welcome_seen', 'true');
    onOpenChange(false);
  };

  const handleNext = () => {
    if (step < features.length - 1) {
      setStep(step + 1);
    } else {
      handleStart();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader className="text-center">
          <DialogTitle className="text-2xl">
            Bem-vindo ao Academy! 🎓
          </DialogTitle>
          <DialogDescription className="text-base">
            Sua jornada para dominar dados começa aqui
          </DialogDescription>
        </DialogHeader>

        <div className="py-6 space-y-4">
          {/* Feature cards */}
          <div className="grid gap-3">
            {features.map((feature, index) => (
              <div 
                key={index}
                className={`flex items-start gap-4 p-4 rounded-xl border transition-all ${
                  index === step 
                    ? 'border-primary bg-primary/5 scale-[1.02]' 
                    : 'border-border/50 opacity-60'
                }`}
              >
                <div className={`p-2 rounded-lg ${feature.color}`}>
                  <feature.icon className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground">{feature.title}</h4>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-2 pt-4">
            {features.map((_, index) => (
              <button
                key={index}
                onClick={() => setStep(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === step ? 'bg-primary w-6' : 'bg-muted-foreground/30'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <Button 
            variant="ghost" 
            className="flex-1"
            onClick={handleStart}
          >
            Pular
          </Button>
          <Button 
            className="flex-1 gap-2"
            onClick={handleNext}
          >
            {step === features.length - 1 ? 'Começar' : 'Próximo'}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function useWelcomeModal() {
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    const hasSeen = localStorage.getItem('academy_welcome_seen');
    if (!hasSeen) {
      // Small delay to let the page load
      const timer = setTimeout(() => setShowWelcome(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  return { showWelcome, setShowWelcome };
}
